import type { Fragment } from "../types.js";
import { brandName, ci, activeColorTheme } from "../../brand/theme.js";
import { escapeHtml } from "../composition.js";

const SUPPORTED_IMG_EXT = [".png", ".jpg", ".jpeg", ".webp", ".svg"];

function resolveLogoSrc(): string | null {
  const logoPath = ci.logo ?? "src/brand/logo.svg";
  const ext = logoPath.toLowerCase().slice(logoPath.lastIndexOf("."));
  if (!SUPPORTED_IMG_EXT.includes(ext)) return null;
  const filename = logoPath.split("/").pop() ?? "logo.svg";
  // Hyperframes serves files relative to the project dir; we copy brand assets
  // into the render workdir under `assets/brand/`. See producer.ts.
  return `assets/brand/${filename}`;
}

/**
 * Mirrors `src/primitives/BrandLogo.tsx`: logo image (with optional white tint)
 * + brand name typography when the logo is icon-only.
 */
export function brandLogo(opts: {
  id: string;
  size?: number;
  showName?: boolean;
  color?: string;
}): Fragment {
  const size = opts.size ?? 120;
  const id = opts.id;
  const isWordmark = ci.logoType === "wordmark";
  const shouldShowName = opts.showName ?? !isWordmark;
  const src = resolveLogoSrc();
  // "white" bedeutet semantisch "Kontrastfarbe auf dem Hintergrund". Im
  // Light-Theme ist das NICHT Weiß (unsichtbar auf hell), sondern dunkle Tinte:
  // Weiß-Tint → `brightness(0)` (schwarz), Textfarbe → CI-fg (dunkel).
  const wantWhite = opts.color === "white";
  const tint = wantWhite
    ? activeColorTheme === "light"
      ? "filter: brightness(0);"
      : "filter: brightness(0) invert(1);"
    : "";
  const textColor = wantWhite
    ? activeColorTheme === "light"
      ? ci.colors.fg
      : "#ffffff"
    : opts.color ?? ci.colors.fg;

  const imgOrFallback = src
    ? `<img src="${escapeHtml(src)}" alt="${escapeHtml(brandName)}" style="height:${size}px;width:auto;object-fit:contain;${tint}" />`
    : `<div style="width:${size}px;height:${size}px;border-radius:${Math.round(size / 4)}px;background:${ci.colors.primary};"></div>`;

  const nameSpan = shouldShowName
    ? `<span style="font-weight:800;font-size:${Math.round(size * 0.55)}px;letter-spacing:-1px;color:${textColor};">${escapeHtml(brandName)}</span>`
    : "";

  const html = `<div id="${id}" class="brand-logo" style="display:flex;align-items:center;gap:18px;">
  ${imgOrFallback}
  ${nameSpan}
</div>`;

  return { html };
}
